import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  CheckSquare,
  FileText,
  History,
  Users,
  Package,
  BarChart3,
  Settings as SettingsIcon,
  Sun,
  Moon,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { Sheet, SheetContent } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import KretaraLogo from "./KretaraLogo";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/requests", label: "Requests", icon: ClipboardList, testid: "nav-requests" },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, testid: "nav-approvals", roles: ["admin", "manager"] },
  { to: "/purchase-orders", label: "Create PO", icon: FileText, testid: "nav-po-create" },
  { to: "/po-history", label: "PO History", icon: History, testid: "nav-po-history" },
  { to: "/vendors", label: "Vendors", icon: Users, testid: "nav-vendors" },
  { to: "/inventory", label: "Inventory", icon: Package, testid: "nav-inventory" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

function NavItem({ item, onClick, role }) {
  const { icon: Icon } = item;
  if (item.roles && !item.roles.includes(role)) return null;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onClick}
      data-testid={item.testid}
      className={({ isActive }) =>
        `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all k-btn-press ` +
        (isActive
          ? "bg-[#534AB7]/12 text-foreground font-medium"
          : "text-muted-foreground hover:text-foreground hover:bg-foreground/5")
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r bg-[#534AB7]" />
          )}
          <Icon size={17} className={isActive ? "text-[#534AB7]" : ""} />
          <span>{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function SidebarContent({ role, onItemClick, onClose }) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between p-4 border-b border-black/5 dark:border-white/10">
        <KretaraLogo size={34} />
        {onClose && (
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-foreground/5" aria-label="Close menu">
            <X size={18} />
          </button>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {NAV.map((it) => (
          <NavItem key={it.to} item={it} role={role} onClick={onItemClick} />
        ))}
      </nav>
      <div className="p-3 border-t border-black/5 dark:border-white/10">
        <div className="k-glass rounded-xl p-3 text-xs text-muted-foreground">
          <div className="font-medium text-foreground" style={{ fontFamily: "Outfit" }}>Kretara.ai v1.0</div>
          <div>Procurement Intelligence</div>
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const role = user?.role;

  return (
    <div className="min-h-screen k-aurora-light dark:k-aurora">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex fixed inset-y-0 left-0 w-64 z-30 k-glass-strong border-r border-black/5 dark:border-white/10"
        data-testid="sidebar"
      >
        <SidebarContent role={role} />
      </aside>

      {/* Mobile sheet drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 k-glass-strong">
          <SidebarContent role={role} onItemClick={() => setMobileOpen(false)} onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Top bar (compact, only icons + avatar) */}
      <header
        className="sticky top-0 z-20 lg:pl-64 backdrop-blur-2xl bg-[#F0EFF8]/70 dark:bg-[#1A1830]/70 border-b border-black/5 dark:border-white/10"
        data-testid="app-header"
      >
        <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-foreground/5 transition"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-trigger"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <div className="lg:hidden">
            <KretaraLogo size={30} withText={false} />
          </div>
          <div className="hidden lg:block">
            {/* spacer so right items align to the right */}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={toggle}
              className="p-2 rounded-lg hover:bg-foreground/5 transition k-btn-press"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-foreground/5 transition"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.avatar} alt={user?.name} />
                    <AvatarFallback className="bg-[#534AB7] text-white text-xs">
                      {(user?.name || "U").slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left leading-tight pr-1">
                    <div className="text-sm font-medium">{user?.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{user?.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <SettingsIcon size={14} className="mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="lg:pl-64" data-testid="app-main">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
